import React from 'react';
import { getData, manageData } from '../../requests/CRUD';
import {LENDERS, VENDORS} from '../../consts/tables';
import Dropdown from "../Dropdown/Dropdown";
import PropTypes from "prop-types";
import Input from "../Input-new/Input";
import { executeBackendQuery } from '../../requests/executeBackendQuery';
import { VENDOR_LIST } from '../../consts/queries';
import filter from '../../filter';
import { matchLenderByName } from '../../utils/matchLenderByName';
import { sortVendorsAlphabetically, getVendorDisplayName, getVendorCompanyName, findVendorByName } from '../SelectEscrowOfficer/SelectEscrowOfficer';
import OpenInCrmButton from '../OpenInCrmButton/OpenInCrmButton';

const cloneDeep = require('lodash.clonedeep');
const limit = 1000;
const CONTACT_LOAD_FAILED = 'Could not load the saved contact';

class SelectLender extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            items: [],
            vendors: [],
            lender: {},
            // The vendor saved as the property's LENDER_CONTACT_ID; the saved id a
            // lookup found no vendor for (deleted since), or could not read.
            contactVendor: null,
            missingContactId: null,
            failedContactId: null,
        };

        this.getSelectedOption = this.getSelectedOption.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleContactChange = this.handleContactChange.bind(this);
    }

    componentDidMount() {
        this.fetch(0);
        if (this.props.withContact) {
            this.fetchLenderVendors();
            this.loadContactVendor(this.props.data.LENDER_CONTACT_ID);
        }
    }

    componentDidUpdate(prevProps) {
        if (!this.props.withContact) return;
        const contactId = this.props.data.LENDER_CONTACT_ID;
        if (String(prevProps.data.LENDER_CONTACT_ID || '') !== String(contactId || '')) {
            this.loadContactVendor(contactId);
        }
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    /** The saved contact may no longer be a Lender vendor, so it is read by id. */
    loadContactVendor(contactId) {
        if (!contactId) {
            this.setState({ contactVendor: null, missingContactId: null, failedContactId: null });
            return;
        }
        const { contactVendor } = this.state;
        if (contactVendor && String(contactVendor.ARCIMEDES_ROW_ID) === String(contactId)) return;

        this.setState({ contactVendor: null, missingContactId: null, failedContactId: null });
        const isStill = () => !this.unmounted && String(this.props.data.LENDER_CONTACT_ID || '') === String(contactId);
        getData({
            tableCode: VENDORS,
            options: {
                filter: [{ text: String(contactId), code: 'ARCIMEDES_ROW_ID', type: 'exact' }],
            },
        }).then((res) => {
            if (!isStill()) return;
            if (!res || !res.success) {
                this.setState({ failedContactId: String(contactId) });
                return;
            }
            const row = Array.isArray(res.data) && res.data.length ? res.data[0] : null;
            this.setState({ contactVendor: row, missingContactId: row ? null : String(contactId) });
        }, () => {
            if (isStill()) this.setState({ failedContactId: String(contactId) });
        });
    }

    /** Same Lender-vendor pool the Loan Quotes tab's Lender Contact uses. */
    fetchLenderVendors() {
        const options = filter({ VENDOR_TYPE: 'Lender' }, 'vendor');
        options.param.limit = 5000;
        options.param.offset = 0;
        executeBackendQuery({ queryName: VENDOR_LIST, options, expireTime: 300 }).then(res => {
            const vendors = sortVendorsAlphabetically((res && res.data || []).filter(v => v.DELETED !== '1'));
            this.setState({ vendors });
        });
    }

    fetch(offset) {
        getData({
            tableCode: LENDERS,
            options: {
                limit: {
                    count: limit,
                    offset: offset * limit,
                },
                column: [
                    'ARCIMEDES_ROW_ID',
                    'NAME',
                    'CONTACT_NAME',
                ],
                filter: [{
                    code: 'NAME',
                    text: '',
                    type: 'notNull',
                }, {
                    code: 'DELETED',
                    text: '',
                    type: 'null',
                }],
                filterType: 'and',
                order: [{
                    code: 'NAME',
                    type: 'asc',
                }],
            },
        }).then(res => {
            this.setState(prevState => {
                const items = prevState.items.concat(res.data);
                return {
                    items,
                };
            });
            if (res.data.length >= limit) {
                this.fetch(++offset);
            }
        });
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.newLender !== nextProps.newLender) {
            const items = cloneDeep(this.state.items);
            items.push(nextProps.newLender);
            this.setState({
                items,
                lender: nextProps.newLender,
            });
        }

        if (this.props.data.LENDER_ID !== nextProps.data.LENDER_ID) {
                // clear the lender contact when no lender is selected
                const option = this.state.items.find(item => item.ARCIMEDES_ROW_ID === nextProps.data.LENDER_ID) || {};
                this.setState({lender: option});
        }
    }

    /** Full LENDERS record for an option that only carries the id. */
    fullLender(option) {
        if (!option || !option.ARCIMEDES_ROW_ID) return option || {};
        return this.state.items.find((i) => String(i.ARCIMEDES_ROW_ID) === String(option.ARCIMEDES_ROW_ID)) || option;
    }

    getSelectedOption(option) {
        if (option && !Object.keys(this.state.lender).length)
            this.setState({lender: this.fullLender(option)});
    }

    /**
     * The property references a LENDERS row, but the offered names come from the
     * vendor pool — a company may have no LENDERS record yet. Create it on the
     * fly so a pick always lands on a LENDER_ID.
     */
    createLender(name) {
        return manageData({ tableCode: LENDERS, data: [{ NAME: name }] }).then((res) => {
            const created = res && res.success && res.data && res.data[0];
            if (!created || !created.ARCIMEDES_ROW_ID) return null;
            this.setState((prev) => ({ items: [...prev.items, created] }));
            return created;
        });
    }

    /** Lender and contact are written together: a contact belongs to the bank it was picked with. */
    setLenderOnProperty(data, optionsType, lenderRecord, contactVendor = null) {
        data.LENDER_ID = lenderRecord.ARCIMEDES_ROW_ID;
        data.LENDER_CONTACT_ID = contactVendor ? contactVendor.ARCIMEDES_ROW_ID : null;
        this.setState({ lender: lenderRecord, contactVendor, missingContactId: null, failedContactId: null });
        this.props.handleParentChange(data, optionsType, lenderRecord.ARCIMEDES_ROW_ID, 'LENDER_ID', 'LENDER_CONTACT_ID');
    }

    setContactOnProperty(data, optionsType, contactVendor) {
        data.LENDER_CONTACT_ID = contactVendor ? contactVendor.ARCIMEDES_ROW_ID : null;
        this.setState({ contactVendor, missingContactId: null, failedContactId: null });
        this.props.handleParentChange(data, optionsType, data.LENDER_CONTACT_ID, 'LENDER_CONTACT_ID');
    }

    handleChange(data, optionsType, value, valueKey, typeKey, option) {
        if (this.props.withContact && option && !option.ARCIMEDES_ROW_ID && option.NAME) {
            this.createLender(option.NAME).then((created) => {
                if (created) this.setLenderOnProperty(data, optionsType, created);
            });
            return;
        }

        this.setState({lender: this.fullLender(option)});

        if (!this.props.withContact) {
            this.props.handleParentChange(data, optionsType, value, valueKey, typeKey);
            return;
        }

        // Picking a lender directly drops the saved contact, so the display
        // falls back to this lender's own contact.
        data.LENDER_CONTACT_ID = null;
        this.setState({ contactVendor: null, missingContactId: null, failedContactId: null });
        this.props.handleParentChange(data, optionsType, value, valueKey, 'LENDER_CONTACT_ID');
    }

    // A contact pick saves that vendor as the property's contact and re-points
    // the Lender to the vendor's company — matching an existing LENDERS record
    // or creating one — the same contact-fills-company behavior as the Loan
    // Quotes tab.
    handleContactChange(data, optionsType, value, valueKey, typeKey, option) {
        delete data.LENDER_CONTACT;
        if (!option) {
            this.setContactOnProperty(data, optionsType, null);
            return;
        }
        // The name already on show, kept as an option though it is no vendor.
        const vendor = option.vendor;
        if (!vendor) return;

        const company = option.COMPANY;
        if (!company) {
            this.setContactOnProperty(data, optionsType, vendor);
            return;
        }
        const matched = matchLenderByName(this.state.items, company);
        if (matched) {
            this.setLenderOnProperty(data, optionsType, matched, vendor);
            return;
        }
        this.createLender(company).then((created) => {
            if (created) this.setLenderOnProperty(data, optionsType, created, vendor);
        });
    }

    render() {
        const { items, vendors, lender, contactVendor, missingContactId, failedContactId } = this.state;
        const {
            theme,
            value,
            valueKey,
            label,
            disabled,
            data,
            withAddNew,
            isFilter,
            viewMode,
            addNewItem,
            deleted,
            isFromProperty,
            withContact,
            contactOverride,
            className,
            // Not for the Dropdown: without pulling these out, the {...rest}
            // spread would override handleParentChange={this.handleChange}.
            handleParentChange,
            newLender,
            ...rest
        } = this.props;

        // Lender Contact is a dropdown of the same Lender-type VENDORS pool the
        // Loan Quotes tab uses, so both tabs offer the same contacts. The open
        // list shows "Contact - Lender" so same-named contacts are tellable
        // apart; the selected value shows just the name — the company lands in
        // the Lender field below. Picking one re-points the Lender to the
        // vendor's company when a LENDERS record matches (handleContactChange).
        const contactOptions = (vendors || [])
            .map((v) => ({ NAME: getVendorDisplayName(v), COMPANY: getVendorCompanyName(v), vendor: v }))
            .filter((o) => o.NAME);

        // The saved contact first, then the approved quote's contact, then the
        // selected lender's own contact on file. A saved contact still being
        // looked up shows nothing rather than one of the others.
        const savedContactId = withContact && data ? data.LENDER_CONTACT_ID : null;
        const savedContact = savedContactId && contactVendor && String(contactVendor.ARCIMEDES_ROW_ID) === String(savedContactId)
            ? contactVendor
            : null;
        const overrideContact = contactOverride != null ? String(contactOverride).trim() : '';
        let contactValue = '';
        let contactLeadId = null;
        if (savedContact) {
            contactValue = getVendorDisplayName(savedContact);
            contactLeadId = savedContact.LEAD_ID;
        } else if (savedContactId && failedContactId === String(savedContactId)) {
            contactValue = CONTACT_LOAD_FAILED;
        } else if (!savedContactId || missingContactId === String(savedContactId)) {
            contactValue = overrideContact || (lender && lender.CONTACT_NAME) || '';
            // Same-named people link only to the one at this bank: a wrong Kore record is worse than none.
            // The matcher reads text with no letters or digits as the start of every name.
            const hasWords = (text) => /[a-z0-9]/i.test(text || '');
            const lenderName = String(this.fullLender({ ARCIMEDES_ROW_ID: value }).NAME || '').trim();
            const atThisBank = (v) => {
                const company = getVendorCompanyName(v);
                return hasWords(lenderName) && hasWords(company) && !!matchLenderByName([{ NAME: lenderName }], company, { contains: false });
            };
            const namedVendor = findVendorByName(vendors, contactValue, atThisBank);
            contactLeadId = namedVendor ? namedVendor.LEAD_ID : null;
        }

        // The value shown is its own option, unless it is the saved vendor's: MUI
        // ignores a pick of the option already selected, so a same-named vendor
        // could not be picked (and saved).
        const firstNamesake = contactOptions.find((o) => o.NAME === contactValue);
        const showsSavedVendor = !!(savedContact && firstNamesake && firstNamesake.vendor
            && String(firstNamesake.vendor.ARCIMEDES_ROW_ID) === String(savedContact.ARCIMEDES_ROW_ID));
        if (contactValue && !showsSavedVendor) {
            contactOptions.unshift({ NAME: contactValue, COMPANY: '' });
        }

        // The Lender dropdown offers the same names Loan Quotes shows: the Lender
        // vendors' companies. Each maps to its LENDERS record when one exists
        // (id null otherwise — handleChange creates the record on pick). Cached:
        // the fuzzy record matching is too heavy to redo every render.
        let lenderOptions = items;
        if (withContact) {
            if (!this._lenderOptionsCache || this._lenderOptionsCache.items !== items || this._lenderOptionsCache.vendors !== vendors) {
                const seen = new Set();
                const built = [];
                (vendors || []).forEach((v) => {
                    const company = getVendorCompanyName(v);
                    if (!company || seen.has(company.toLowerCase())) return;
                    seen.add(company.toLowerCase());
                    const matched = matchLenderByName(items, company);
                    built.push({ NAME: company, ARCIMEDES_ROW_ID: matched ? matched.ARCIMEDES_ROW_ID : null });
                });
                built.sort((a, b) => a.NAME.localeCompare(b.NAME, undefined, { sensitivity: 'base' }));
                this._lenderOptionsCache = { items, vendors, options: built };
            }
            lenderOptions = this._lenderOptionsCache.options;

            // The saved lender still has to display/select even when it isn't
            // any vendor's company (legacy records, drawer-added lenders).
            if (value && !lenderOptions.some((o) => String(o.ARCIMEDES_ROW_ID) === String(value))) {
                const current = items.find((i) => String(i.ARCIMEDES_ROW_ID) === String(value));
                if (current) lenderOptions = [{ NAME: current.NAME, ARCIMEDES_ROW_ID: current.ARCIMEDES_ROW_ID }, ...lenderOptions];
            }
        }

        // Lender Contact renders first, the Lender second (Claritask 548290).
        return (
            <React.Fragment>
                {
                    withContact &&
                    <Dropdown theme={theme}
                              className={className}
                              optionsType='lender-contact'
                              optionKey='NAME'
                              options={contactOptions}
                              renderOption={(option) => option && option.COMPANY ? `${option.NAME} - ${option.COMPANY}` : ((option && option.NAME) || '')}
                              value={contactValue}
                              valueKey='LENDER_CONTACT'
                              label='Lender Contact'
                              disabled={disabled}
                              data={data}
                              isFilter={isFilter}
                              handleParentChange={this.handleContactChange}
                              viewModeExtra={contactLeadId ? <OpenInCrmButton leadId={contactLeadId} entityType="account" variant="link" /> : null}
                              viewMode={viewMode}
                              deleted={deleted}
                              isFromProperty={isFromProperty}
                    />
                }
                <Dropdown theme={theme}
                          className={className}
                          optionsType='lender'
                          optionKey='ARCIMEDES_ROW_ID'
                          options={lenderOptions}
                          value={value}
                          valueKey={valueKey}
                          label={label}
                          disabled={!items || disabled}
                          data={data}
                          isFilter={isFilter}
                          handleParentChange={this.handleChange}
                          withAddNew={withAddNew}
                          viewMode={viewMode}
                          deleted={deleted}
                          addNewItem={addNewItem}
                          isFromProperty={isFromProperty}
                          getSelectedOption={this.getSelectedOption}
                          {...rest}
                />
            </React.Fragment>
        )
    }
}

SelectLender.propTypes = {
    theme: PropTypes.oneOf(['default', 'white', 'dark']),
    value: PropTypes.string,
    valueKey: PropTypes.string.isRequired,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    data: PropTypes.object,
    withAddNew: PropTypes.bool,
    handleParentChange: PropTypes.func.isRequired,
    isFilter: PropTypes.bool,
    viewMode: PropTypes.bool,
    addNewItem: PropTypes.func,
    deleted: PropTypes.bool,
    isFromProperty: PropTypes.bool,
    withContact: PropTypes.bool,
    className: PropTypes.string,
};

export default SelectLender;
